import { PaymentMethodEnum } from "@prostocheck/shared";

import {
  CARD_PAYMENT_CODE,
  CARD_PAYMENT_PATTERN,
  CASH_PAYMENT_CODE,
  CASH_PAYMENT_PATTERN,
  DOCUMENT_ENCODING,
  ORDER_DATE_LENGTH,
  ORDER_TIME_LENGTH,
  XML_MIME_TYPE,
} from "./document-xml.constants";

/**
 * Turns the Base64 `Data` of a CheckExt / ZRepExt response into a DOM.
 * The bytes are decoded as Windows-1251 before parsing: the XML declares that
 * encoding, but `DOMParser` only ever sees the string we hand it.
 */
export const parseDocumentXml = (base64: string): Document => {
  const binary = globalThis.atob(base64);
  const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
  const xml = new TextDecoder(DOCUMENT_ENCODING).decode(bytes);

  return new DOMParser().parseFromString(xml, XML_MIME_TYPE);
};

/** First `<tag>` anywhere under `scope`; documents are shallow enough for this. */
export const readText = (scope: ParentNode, tag: string): string =>
  scope.querySelector(tag)?.textContent?.trim() ?? "";

export const readNumber = (scope: ParentNode, tag: string): number => {
  const value = Number(readText(scope, tag).replace(",", "."));

  if (!Number.isFinite(value)) return 0;

  return value;
};

export const readBoolean = (scope: ParentNode, tag: string): boolean =>
  readText(scope, tag).toLowerCase() === "true";

/** `<ROW>` elements of a container tag, in document order. */
export const readRows = (
  scope: ParentNode,
  containerTag: string,
): Element[] => {
  const container = scope.querySelector(containerTag);

  if (!container) return [];

  return [...container.querySelectorAll("ROW")];
};

/**
 * `<ORDERDATE>DDMMYYYY</ORDERDATE>` + `<ORDERTIME>HHMMSS</ORDERTIME>` are the
 * wall-clock time the register printed the document, so they are read as local
 * time — the same convention `buildTaxCabinetCheckUrl` reverses.
 */
export const toIsoTimestamp = ({
  date,
  time,
}: {
  date: string;
  time: string;
}): string => {
  if (date.length !== ORDER_DATE_LENGTH || time.length !== ORDER_TIME_LENGTH) {
    return "";
  }

  const day = Number(date.slice(0, 2));
  const month = Number(date.slice(2, 4));
  const year = Number(date.slice(4, 8));
  const hours = Number(time.slice(0, 2));
  const minutes = Number(time.slice(2, 4));
  const seconds = Number(time.slice(4, 6));

  return new Date(year, month - 1, day, hours, minutes, seconds).toISOString();
};

/** Rounds to kopiykas; sums of parsed decimals drift otherwise. */
export const toMoney = (value: number): number => Number(value.toFixed(2));

/**
 * Classifies one `<ROW>` of a payment-forms list. The form is free text chosen
 * by the ПРРО vendor, so the name decides and the code only breaks ties.
 * Anything unrecognised counts as card rather than cash: an overstated cash
 * figure is the worse error for a business reconciling a till.
 */
export const readPaymentMethod = (row: Element): PaymentMethodEnum => {
  const name = readText(row, "PAYFORMNM");

  if (CASH_PAYMENT_PATTERN.test(name)) return PaymentMethodEnum.Cash;
  if (CARD_PAYMENT_PATTERN.test(name)) return PaymentMethodEnum.Card;

  const code = readText(row, "PAYFORMCD");

  if (code === CASH_PAYMENT_CODE) return PaymentMethodEnum.Cash;
  if (code === CARD_PAYMENT_CODE) return PaymentMethodEnum.Card;

  return PaymentMethodEnum.Card;
};
